/**
 * Migrate to TOTP Form Component
 * Form for migrating OAuth accounts to TOTP-based automated authentication
 */

import React, { useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Link,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Info as InfoIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { migrateToTOTP } from '@shared/features/broker-accounts/services/brokerAccountService';

interface MigrateToTOTPFormProps {
  registrationId: string;
  accountName: string;
  brokerName: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export function MigrateToTOTPForm({
  registrationId,
  accountName,
  brokerName,
  onSuccess,
  onCancel,
}: MigrateToTOTPFormProps) {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpSecret, setTotpSecret] = useState('');
  const [showTotpSecret, setShowTotpSecret] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const shortName = brokerName.split(' ')[0];

  const validateTOTPSecret = (secret: string): boolean => {
    const cleaned = secret.replace(/\s/g, '').toUpperCase();
    return cleaned.length >= 16 && /^[A-Z2-7]+$/.test(cleaned);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!password.trim()) {
      const passLabel = shortName.toLowerCase() === 'dhan' ? 'Dhan Pin' : (shortName.toLowerCase() === 'groww' ? 'TOTP Token' : 'Password');
      setError(`${shortName} ${passLabel} is required`);
      return;
    }
    if (!totpSecret.trim()) {
      setError(`${shortName} TOTP Secret is required`);
      return;
    }

    // Validate TOTP secret format
    const cleanedSecret = totpSecret.replace(/\s/g, '').toUpperCase();
    if (!validateTOTPSecret(cleanedSecret)) {
      setError('TOTP secret must be at least 16 characters and in base32 format (A-Z, 2-7)');
      return;
    }

    try {
      setLoading(true);
      await migrateToTOTP(registrationId, password, cleanedSecret);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to migrate account to TOTP');
    } finally {
      setLoading(false);
    }
  };

  const handleTOTPSecretChange = (value: string) => {
    const cleaned = value.replace(/\s/g, '').toUpperCase();
    setTotpSecret(cleaned);
  };

  return (
    <Dialog open={true} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon color="primary" />
          Migrate to Automated Login
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Alert severity="info" icon={<InfoIcon />}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Enable Automated Login:</strong> Migrate your account <strong>{accountName}</strong> from manual OAuth login to automated {shortName} TOTP-based authentication.
            </Typography>
            <Typography variant="body2">
              After migration, your account will be automatically reconnected when tokens expire. Your existing access token will be preserved.
            </Typography>
          </Alert>

          <TextField
            label={shortName.toLowerCase() === 'dhan' ? `${shortName} Pin` : (shortName.toLowerCase() === 'groww' ? `${shortName} TOTP Token` : `${shortName} Password`)}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            size="small"
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    size="small"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="TOTP Secret"
            type={showTotpSecret ? 'text' : 'password'}
            value={totpSecret}
            onChange={(e) => handleTOTPSecretChange(e.target.value)}
            placeholder="e.g., JBSWY3DPEHPK3PXP"
            fullWidth
            required
            size="small"
            disabled={loading}
            helperText={
              <>
                Base32-encoded TOTP secret (minimum 16 characters).
                <Link
                  href="https://kite.trade/apps"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ml: 0.5 }}
                >
                  Find it in Kite App settings
                </Link>
              </>
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowTotpSecret(!showTotpSecret)}
                    edge="end"
                    size="small"
                  >
                    {showTotpSecret ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            inputProps={{
              style: { fontFamily: 'monospace', fontSize: '0.875rem' },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !password.trim() || !totpSecret.trim()}
          startIcon={loading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
        >
          {loading ? 'Migrating...' : 'Migrate to Automated Login'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

